import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { SearchCompareHeader } from './SearchCompareHeader';
import {
  compareAttributesListFood,
  compareAttributesListIngredients,
  compareSupplierListIngredients,
  getCompanyInfoByProperty,
  getIngredientInfoByProperty
} from 'helpers/Search';
import { IS_UXBEAUTY } from 'components/constants/Constants';
import { ProductType } from 'types/Products';
import { SearchDocument } from 'types/Search';
import Close from '@mui/icons-material/Close';
import { tss } from 'tss-react/mui';

interface SearchCompareModalProps {
  show: boolean;
  onHide: () => void;
  compareItemList: SearchDocument[];
  onRemoveItemClick: (itemId: string) => void;
  searchRequest?: any;
  target: ProductType;
}

const SearchCompareModal = ({
  show = false,
  onHide,
  compareItemList,
  onRemoveItemClick,
  searchRequest
}: SearchCompareModalProps) => {
  const [expandedAttributes, setExpandedAttributes] = useState(true);
  const [expandedSupplier, setExpandedSupplier] = useState(true);
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(false);
  const [expandedDescription, setExpandedDescription] = useState(false);

  const compareAttributesList =
    searchRequest.segment === 'FOOD'
      ? compareAttributesListFood
      : compareAttributesListIngredients;
  const { classes } = useStyles();
  return (
    <Dialog onClose={onHide} open={show} maxWidth="xl" fullWidth>
      <DialogTitle className={classes.lightBackGround}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h3">Compare Ingredients</Typography>
          <IconButton onClick={onHide}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent className={classes.lightBackGround} sx={{ px: 0 }}>
        <TableContainer component={Paper}>
          <Table
            sx={{
              '& th:nth-of-type(1), & td:nth-of-type(1)': {
                maxWidth: 200,
                width: 200
              },
              tableLayout: { xs: 'auto', md: 'fixed' },
              '& .MuiTableCell-root': {
                border: ({ palette }) => `1px solid ${palette.divider}`,
                flex: 1
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  className={classes.lightBackGround}
                  sx={{ verticalAlign: 'top' }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showOnlyDifferences}
                        onChange={() =>
                          setShowOnlyDifferences(!showOnlyDifferences)
                        }
                      />
                    }
                    label="Show only differences"
                  />
                </TableCell>
                {compareItemList.map(compareItem => (
                  <TableCell key={compareItem.id} sx={{ verticalAlign: 'top' }}>
                    <SearchCompareHeader
                      compareItem={compareItem}
                      searchRequest={searchRequest}
                      onRemoveItemClick={onRemoveItemClick}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={classes.lightBackGround}>
                  <Button
                    variant="contained"
                    onClick={() => setExpandedDescription(!expandedDescription)}
                  >
                    {expandedDescription ? (
                      <KeyboardArrowUp fontSize="small" />
                    ) : (
                      <KeyboardArrowDown fontSize="small" />
                    )}
                    Description
                  </Button>
                </TableCell>
                {expandedDescription &&
                  compareItemList.map(compareItem => (
                    <TableCell key={compareItem.id}>
                      <Typography>
                        {compareItem.marketing.description}
                      </Typography>
                    </TableCell>
                  ))}
              </TableRow>

              <>
                {!IS_UXBEAUTY && (
                  <TableRow>
                    <TableCell className={classes.lightBackGround}>
                      <Button
                        variant="contained"
                        onClick={() =>
                          setExpandedAttributes(!expandedAttributes)
                        }
                      >
                        {expandedAttributes ? (
                          <KeyboardArrowUp fontSize="small" />
                        ) : (
                          <KeyboardArrowDown fontSize="small" />
                        )}
                        Attributes
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
                {expandedAttributes &&
                  compareAttributesList
                    .filter(
                      attributeItem =>
                        !showOnlyDifferences ||
                        new Set(
                          compareItemList.map(compareItem =>
                            getIngredientInfoByProperty(
                              attributeItem.key,
                              compareItem
                            )
                          )
                        ).size > 1
                    )
                    .map(attributeItem => (
                      <TableRow key={attributeItem.key}>
                        <TableCell className={classes.lightBackGround}>
                          {attributeItem.label}
                        </TableCell>
                        {compareItemList.map(compareItem => (
                          <TableCell key={compareItem.key}>
                            {getIngredientInfoByProperty(
                              attributeItem.key,
                              compareItem
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
              </>

              {!IS_UXBEAUTY && (
                <>
                  <TableRow>
                    <TableCell className={classes.lightBackGround}>
                      <Button
                        variant="contained"
                        onClick={() => setExpandedSupplier(!expandedSupplier)}
                      >
                        {expandedSupplier ? (
                          <KeyboardArrowUp fontSize="small" />
                        ) : (
                          <KeyboardArrowDown fontSize="small" />
                        )}
                        Supplier
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expandedSupplier &&
                    !IS_UXBEAUTY &&
                    compareSupplierListIngredients
                      .filter(
                        supplierAttribute =>
                          !showOnlyDifferences ||
                          new Set(
                            compareItemList.map(compareItem =>
                              getCompanyInfoByProperty(
                                supplierAttribute.key,
                                compareItem
                              )
                            )
                          ).size > 1
                      )
                      .map(supplierAttribute => (
                        <TableRow key={supplierAttribute.key}>
                          <TableCell className={classes.lightBackGround}>
                            {supplierAttribute.label}
                          </TableCell>
                          {compareItemList.map(compareItem => (
                            <TableCell key={compareItem.key}>
                              {getCompanyInfoByProperty(
                                supplierAttribute.key,
                                compareItem
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = tss.create(({ theme }) => ({
  lightBackGround: {
    backgroundColor: theme.palette.lightBackground
  }
}));
export default SearchCompareModal;
